import React, { useState, useEffect, useRef } from 'react';
import { theme } from '../styles/theme';
import { Button } from './Button';

export const Header = ({ showAuth = true }) => {
  const [userRole, setUserRole] = useState<string | null>(null);
  const [userName, setUserName] = useState<string | null>(null);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [pendingApplications, setPendingApplications] = useState(0);
  const [queryResponses, setQueryResponses] = useState(0);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Check if user is logged in and get their role
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        const user = JSON.parse(storedUser);
        setUserRole(user.role);
        setUserName(user.name || user.email);
      } catch (err) {
        console.error('Error parsing user data:', err);
      }
    }
  }, []);

  useEffect(() => {
    // Close dropdown when clicking outside
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogoClick = () => {
    if (userRole === 'WRITER') {
      window.location.href = '/writer/dashboard';
    } else if (userRole === 'READER') {
      window.location.href = '/reader/dashboard';
    } else if (userRole === 'AGENT') {
      window.location.href = '/agent/dashboard';
    } else {
      window.location.href = '/';
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    window.location.href = '/';
  };

  const toggleDropdown = (dropdown: string) => {
    setOpenDropdown(openDropdown === dropdown ? null : dropdown);
  };

  const Dropdown = ({ label, items, badge }: { label: string; items: Array<{ label: string; href: string; badge?: number }>; badge?: number }) => {
    const dropdownKey = label.toLowerCase().replace(/\s+/g, '-');
    const isOpen = openDropdown === dropdownKey;

    return (
      <div style={{ position: 'relative' }}>
        <button
          onClick={() => toggleDropdown(dropdownKey)}
          style={{
            background: 'none',
            border: 'none',
            color: theme.colors.textSecondary,
            fontSize: '16px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            padding: '8px 12px',
            borderRadius: theme.borderRadius.small,
            transition: 'all 0.2s',
            fontWeight: '500',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = theme.colors.backgroundAlt;
            e.currentTarget.style.color = theme.colors.textPrimary;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
            e.currentTarget.style.color = theme.colors.textSecondary;
          }}
        >
          {label}
          {badge !== undefined && badge > 0 && (
            <span style={{
              backgroundColor: theme.colors.primary,
              color: theme.colors.white,
              borderRadius: '12px',
              padding: '2px 8px',
              fontSize: '12px',
              fontWeight: '600',
              marginLeft: '4px',
            }}>
              {badge}
            </span>
          )}
          <span style={{ fontSize: '10px', opacity: 0.6 }}>▼</span>
        </button>
        {isOpen && (
          <div style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            backgroundColor: theme.colors.white,
            border: `1px solid ${theme.colors.border}`,
            borderRadius: theme.borderRadius.large,
            marginTop: '12px',
            minWidth: '220px',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.08)',
            zIndex: 1000,
            overflow: 'hidden',
          }}>
            {items.map((item, index) => (
              <a
                key={index}
                href={item.href}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '14px 18px',
                  color: theme.colors.textPrimary,
                  textDecoration: 'none',
                  fontSize: '15px',
                  fontWeight: '500',
                  transition: 'all 0.15s ease',
                  borderBottom: index < items.length - 1 ? `1px solid ${theme.colors.border}` : 'none',
                }}
                onMouseEnter={(e) => {
                  (e.target as HTMLElement).style.backgroundColor = theme.colors.primaryLight;
                  (e.target as HTMLElement).style.paddingLeft = '22px';
                }}
                onMouseLeave={(e) => {
                  (e.target as HTMLElement).style.backgroundColor = 'transparent';
                  (e.target as HTMLElement).style.paddingLeft = '18px';
                }}
              >
                {item.label}
                {item.badge !== undefined && item.badge > 0 && (
                  <span style={{
                    backgroundColor: theme.colors.primary,
                    color: theme.colors.white,
                    borderRadius: '12px',
                    padding: '3px 8px',
                    fontSize: '12px',
                    fontWeight: '600',
                  }}>
                    {item.badge}
                  </span>
                )}
              </a>
            ))}
          </div>
        )}
      </div>
    );
  };

  const AvatarDropdown = () => {
    const isOpen = openDropdown === 'avatar';

    return (
      <div style={{ position: 'relative' }}>
        <button
          onClick={() => toggleDropdown('avatar')}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: 0,
            transition: 'transform 0.2s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'scale(1.05)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'scale(1)';
          }}
        >
          <div style={{
            width: '36px',
            height: '36px',
            borderRadius: '50%',
            backgroundColor: theme.colors.primary,
            color: theme.colors.white,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '16px',
            fontWeight: '600',
            boxShadow: '0 2px 8px rgba(99, 102, 241, 0.2)',
          }}>
            {userName?.charAt(0).toUpperCase() || 'U'}
          </div>
        </button>
        {isOpen && (
          <div style={{
            position: 'absolute',
            top: '100%',
            right: 0,
            backgroundColor: theme.colors.white,
            border: `1px solid ${theme.colors.border}`,
            borderRadius: theme.borderRadius.large,
            marginTop: '12px',
            minWidth: '200px',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.08)',
            zIndex: 1000,
            overflow: 'hidden',
          }}>
            <div style={{
              padding: '16px 18px',
              borderBottom: `1px solid ${theme.colors.border}`,
              backgroundColor: theme.colors.backgroundAlt,
            }}>
              <div style={{
                fontSize: '15px',
                fontWeight: '600',
                color: theme.colors.textPrimary,
              }}>
                {userName}
              </div>
              <div style={{
                fontSize: '13px',
                color: theme.colors.textTertiary,
                marginTop: '4px',
                textTransform: 'capitalize',
              }}>
                {userRole?.toLowerCase()}
              </div>
            </div>
            <button
              onClick={() => window.location.href = `/${userRole?.toLowerCase()}/profile/edit`}
              style={{
                width: '100%',
                display: 'block',
                padding: '14px 18px',
                background: 'none',
                border: 'none',
                color: theme.colors.textPrimary,
                textDecoration: 'none',
                fontSize: '15px',
                fontWeight: '500',
                textAlign: 'left',
                borderBottom: `1px solid ${theme.colors.border}`,
                transition: 'all 0.15s ease',
                cursor: 'pointer',
              }}
              onMouseEnter={(e) => {
                (e.target as HTMLElement).style.backgroundColor = theme.colors.primaryLight;
                (e.target as HTMLElement).style.paddingLeft = '22px';
              }}
              onMouseLeave={(e) => {
                (e.target as HTMLElement).style.backgroundColor = 'transparent';
                (e.target as HTMLElement).style.paddingLeft = '18px';
              }}
            >
              Profile
            </button>
            <button
              onClick={() => window.location.href = '/settings'}
              style={{
                width: '100%',
                display: 'block',
                padding: '14px 18px',
                background: 'none',
                border: 'none',
                color: theme.colors.textPrimary,
                textDecoration: 'none',
                fontSize: '15px',
                fontWeight: '500',
                textAlign: 'left',
                borderBottom: `1px solid ${theme.colors.border}`,
                transition: 'all 0.15s ease',
                cursor: 'pointer',
              }}
              onMouseEnter={(e) => {
                (e.target as HTMLElement).style.backgroundColor = theme.colors.primaryLight;
                (e.target as HTMLElement).style.paddingLeft = '22px';
              }}
              onMouseLeave={(e) => {
                (e.target as HTMLElement).style.backgroundColor = 'transparent';
                (e.target as HTMLElement).style.paddingLeft = '18px';
              }}
            >
              Settings
            </button>
            <button
              onClick={handleLogout}
              style={{
                width: '100%',
                padding: '14px 18px',
                background: 'none',
                border: 'none',
                color: theme.colors.error,
                textDecoration: 'none',
                fontSize: '15px',
                fontWeight: '500',
                textAlign: 'left',
                cursor: 'pointer',
                transition: 'all 0.15s ease',
              }}
              onMouseEnter={(e) => {
                (e.target as HTMLElement).style.backgroundColor = '#fee';
                (e.target as HTMLElement).style.paddingLeft = '22px';
              }}
              onMouseLeave={(e) => {
                (e.target as HTMLElement).style.backgroundColor = 'transparent';
                (e.target as HTMLElement).style.paddingLeft = '18px';
              }}
            >
              Sign Out
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderWriterNav = () => (
    <>
      <a href="/writer/dashboard" style={{
        color: theme.colors.textSecondary,
        textDecoration: 'none',
        fontSize: '16px',
        padding: '8px 4px',
      }}>
        Dashboard
      </a>
      <a href="/writer/manuscripts" style={{
        color: theme.colors.textSecondary,
        textDecoration: 'none',
        fontSize: '16px',
        padding: '8px 4px',
      }}>
        My Manuscripts
      </a>
      <a href="/writer/beta-readers" style={{
        color: theme.colors.textSecondary,
        textDecoration: 'none',
        fontSize: '16px',
        padding: '8px 4px',
        position: 'relative',
        display: 'inline-block',
      }}>
        Beta Readers
        {pendingApplications > 0 && (
          <span style={{
            position: 'absolute',
            top: '2px',
            right: '-8px',
            backgroundColor: theme.colors.error,
            color: theme.colors.white,
            borderRadius: '10px',
            padding: '2px 6px',
            fontSize: '12px',
            fontWeight: '500',
          }}>
            {pendingApplications}
          </span>
        )}
      </a>
      <a href="/agents" style={{
        color: theme.colors.textSecondary,
        textDecoration: 'none',
        fontSize: '16px',
        padding: '8px 4px',
      }}>
        Agents
      </a>
      <a href="/writer/queries" style={{
        color: theme.colors.textSecondary,
        textDecoration: 'none',
        fontSize: '16px',
        padding: '8px 4px',
        position: 'relative',
        display: 'inline-block',
      }}>
        Queries
        {queryResponses > 0 && (
          <span style={{
            position: 'absolute',
            top: '2px',
            right: '-8px',
            backgroundColor: theme.colors.error,
            color: theme.colors.white,
            borderRadius: '10px',
            padding: '2px 6px',
            fontSize: '12px',
            fontWeight: '500',
          }}>
            {queryResponses}
          </span>
        )}
      </a>
      <a href="/messages" style={{
        color: theme.colors.textSecondary,
        textDecoration: 'none',
        fontSize: '16px',
        padding: '8px 4px',
        position: 'relative',
        display: 'inline-block',
      }}>
        Messages
        {unreadMessages > 0 && (
          <span style={{
            position: 'absolute',
            top: '2px',
            right: '-8px',
            backgroundColor: theme.colors.error,
            color: theme.colors.white,
            borderRadius: '10px',
            padding: '2px 6px',
            fontSize: '12px',
            fontWeight: '500',
          }}>
            {unreadMessages}
          </span>
        )}
      </a>
      <AvatarDropdown />
    </>
  );

  const renderReaderNav = () => (
    <>
      <a href="/reader/dashboard" style={{
        color: theme.colors.textSecondary,
        textDecoration: 'none',
        fontSize: '16px',
      }}>
        Dashboard
      </a>
      <a href="/manuscripts/discover" style={{
        color: theme.colors.textSecondary,
        textDecoration: 'none',
        fontSize: '16px',
      }}>
        Discover
      </a>
      <a href="/reader/applications" style={{
        color: theme.colors.textSecondary,
        textDecoration: 'none',
        fontSize: '16px',
      }}>
        My Applications
      </a>
      <a href="/reader/manuscripts" style={{
        color: theme.colors.textSecondary,
        textDecoration: 'none',
        fontSize: '16px',
      }}>
        Current Reads
      </a>
      <a href="/messages" style={{
        color: theme.colors.textSecondary,
        textDecoration: 'none',
        fontSize: '16px',
        position: 'relative',
      }}>
        Messages
        {unreadMessages > 0 && (
          <span style={{
            position: 'absolute',
            top: '-8px',
            right: '-8px',
            backgroundColor: theme.colors.error,
            color: theme.colors.white,
            borderRadius: '10px',
            padding: '2px 6px',
            fontSize: '12px',
            fontWeight: '500',
          }}>
            {unreadMessages}
          </span>
        )}
      </a>
      <AvatarDropdown />
    </>
  );

  const renderAgentNav = () => (
    <>
      <a href="/agent/dashboard" style={{
        color: theme.colors.textSecondary,
        textDecoration: 'none',
        fontSize: '16px',
      }}>
        Dashboard
      </a>
      <a href="/agent/queries" style={{
        color: theme.colors.textSecondary,
        textDecoration: 'none',
        fontSize: '16px',
      }}>
        Query Inbox
      </a>
      <a href="/agent/clients" style={{
        color: theme.colors.textSecondary,
        textDecoration: 'none',
        fontSize: '16px',
      }}>
        Clients
      </a>
      <a href="/messages" style={{
        color: theme.colors.textSecondary,
        textDecoration: 'none',
        fontSize: '16px',
        position: 'relative',
      }}>
        Messages
        {unreadMessages > 0 && (
          <span style={{
            position: 'absolute',
            top: '-8px',
            right: '-8px',
            backgroundColor: theme.colors.error,
            color: theme.colors.white,
            borderRadius: '10px',
            padding: '2px 6px',
            fontSize: '12px',
            fontWeight: '500',
          }}>
            {unreadMessages}
          </span>
        )}
      </a>
      <AvatarDropdown />
    </>
  );

  const renderPublicNav = () => (
    <>
      <a href="/blog" style={{
        color: theme.colors.textSecondary,
        textDecoration: 'none',
        fontSize: '16px',
      }}>
        Blog
      </a>
      <a href="/manuscripts/discover" style={{
        color: theme.colors.textSecondary,
        textDecoration: 'none',
        fontSize: '16px',
      }}>
        Discover
      </a>
      <a href="/register?type=writer" style={{
        color: theme.colors.textSecondary,
        textDecoration: 'none',
        fontSize: '16px',
      }}>
        For Writers
      </a>
      <a href="/agents" style={{
        color: theme.colors.textSecondary,
        textDecoration: 'none',
        fontSize: '16px',
      }}>
        Find Agents
      </a>
      {showAuth && (
        <Button onClick={() => window.location.href = '/login'}>
          Sign In
        </Button>
      )}
    </>
  );

  return (
    <header style={{
      backgroundColor: theme.colors.white,
      borderBottom: `1px solid ${theme.colors.border}`,
      padding: '20px 40px',
      position: 'sticky',
      top: 0,
      zIndex: 100,
    }}>
      <div
        ref={dropdownRef}
        style={{
          maxWidth: '1400px',
          margin: '0 auto',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '20px',
        }}
      >
        <h1
          onClick={handleLogoClick}
          style={{
            fontSize: '22px',
            fontWeight: '500',
            margin: 0,
            color: theme.colors.textPrimary,
            letterSpacing: '0.03em',
            fontFamily: theme.fonts.monospace,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
          }}
        >
          <img
            src="/ManuscriptMatchLogo.svg"
            alt="Manuscript Match Logo"
            style={{
              width: '32px',
              height: '32px',
            }}
          />
          Manuscript Match
        </h1>
        <nav style={{
          display: 'flex',
          gap: '24px',
          alignItems: 'center',
        }}>
          {userRole === 'WRITER' && renderWriterNav()}
          {userRole === 'READER' && renderReaderNav()}
          {userRole === 'AGENT' && renderAgentNav()}
          {!userRole && renderPublicNav()}
        </nav>
      </div>
    </header>
  );
};
